import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { Box, TableCell, TableRow, Typography, useTheme } from '@mui/material'
import PropTypes from 'prop-types'

import { selectLocale } from '@/app/slices/appSlice'
import { selectCurrencyCode } from '@/app/slices/organisationSlice'
import { DateTime } from '@/common/components'
import { useToolBoxTreatments } from '@/common/hooks/useToolBoxTreatments'
import { getFormattedPrice, ORDER_STATUS_ARRAY } from '@/common/utils'
import OrderPaidStatusChip from '@/features/order/components/common/OrderPaidStatusChip'
import OrderStatusChip from '@/features/order/components/common/OrderStatusChip'
import DownloadDocuments from '@/features/order/components/DownloadDocuments/DownloadDocuments'

const classes = {
    cell: {
        padding: '16px',
        color: 'text.primary',
    },
    row: {
        '&:hover': {
            background: (theme) => theme.palette.action.hover,
        },
    },
    button: {
        margin: '0px 8px',
    },
}

const OrdersHistoryRow = ({ onOrderDuplicateClicked, order }) => {
    const { t } = useTranslation()
    const theme = useTheme()

    const { showDownloadPdfs, showPayments } = useToolBoxTreatments()

    const currencyCode = useSelector(selectCurrencyCode)
    const locale = useSelector(selectLocale)

    const showPaymentStatus = useMemo(() => {
        return [
            ORDER_STATUS_ARRAY.Ordered,
            ORDER_STATUS_ARRAY.Invoiced,
            ORDER_STATUS_ARRAY.Cancelled,
            ORDER_STATUS_ARRAY.Voided,
        ].includes(order?.status)
    }, [order?.status])

    return (
        <TableRow sx={classes.row}>
            <TableCell
                align="left"
                sx={classes.cell}
            >
                <Link
                    style={{ color: theme.palette.secondary.main }}
                    to={`/orders/${order.id}`}
                >
                    {order?.name ?? ''}
                </Link>
            </TableCell>

            <TableCell
                align="left"
                sx={classes.cell}
            >
                {order.rfqNumber ?? ''}
            </TableCell>
            <TableCell
                align="left"
                sx={classes.cell}
            >
                {order.poNumber ?? ''}
            </TableCell>
            <TableCell
                align="right"
                sx={classes.cell}
            >
                {order?.numberOfItems ?? ''}
            </TableCell>
            <TableCell
                align="right"
                sx={classes.cell}
            >
                <Typography variant="strong2">
                    {getFormattedPrice(order.lineTaxedPrice || 0, currencyCode, locale)}
                </Typography>
            </TableCell>
            <TableCell
                align="right"
                sx={classes.cell}
            >
                {order?.requiredDateUtc ? (
                    <DateTime
                        format="DD-MMM-YYYY"
                        locale={locale}
                    >
                        {order.requiredDateUtc}
                    </DateTime>
                ) : (
                    ''
                )}
            </TableCell>
            <TableCell
                align="right"
                sx={classes.cell}
            >
                <DateTime
                    format="DD-MMM-YYYY"
                    locale={locale}
                >
                    {order.createDateUtc}
                </DateTime>
            </TableCell>
            <TableCell
                align="right"
                sx={[classes.cell, { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }]}
            >
                {order.expiryDate ? (
                    <DateTime
                        format="DD-MMM-YYYY"
                        locale={locale}
                    >
                        {order.expiryDate}
                    </DateTime>
                ) : (
                    ''
                )}
            </TableCell>
            <TableCell
                align="right"
                sx={classes.cell}
            >
                {order.orderedDateUtc ? (
                    <DateTime
                        format="DD-MMM-YYYY"
                        locale={locale}
                    >
                        {order.orderedDateUtc}
                    </DateTime>
                ) : (
                    ''
                )}
            </TableCell>
            <TableCell
                align="left"
                sx={classes.cell}
            >
                <OrderStatusChip orderStatus={order.status} />
            </TableCell>

            {/* {showPayments ? (
                <TableCell
                    align="left"
                    sx={classes.cell}
                >
                    {showPaymentStatus ? <OrderPaidStatusChip paymentStatus={order.paymentStatus} /> : null}
                </TableCell>
            ) : null} */}

            <TableCell
                align="right"
                sx={classes.cell}
            >
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        justifyContent: 'flex-end',
                    }}
                >
                    {showDownloadPdfs ? (
                        <DownloadDocuments
                            order={order}
                            autoExport
                            useIconButton
                        />
                    ) : null}
                </Box>
            </TableCell>
        </TableRow>
    )
}

OrdersHistoryRow.propTypes = {
    order: PropTypes.object,
    onOrderDuplicateClicked: PropTypes.func,
}

export default OrdersHistoryRow
